/**
 * Admin Users — list of registered accounts with email/name/created date.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson } from './api';

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function AdminUsers() {
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (query) => {
    setLoading(true); setError('');
    try {
      const qs = query ? `?q=${encodeURIComponent(query)}` : '';
      const r = await apiJson(`/api/admin/users${qs}`, { token });
      setUsers(r.users || []);
    } catch (e) { setError(e.message || 'Failed to load users'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!user?.isAdmin) return;
    load('');
  }, [user, load]);

  function onSearch(e) {
    e.preventDefault();
    load(q.trim());
  }

  if (!user?.isAdmin) return <div className="tt-page"><div className="tt-status error">Admin only</div></div>;

  return (
    <div className="tt-page" style={{ maxWidth: 1100 }}>
      <header className="tt-header">
        <div>
          <h1>Users</h1>
          <p className="tt-sub">Registered accounts. Showing up to 500 most recent.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/admin" className="secondary-btn">← Admin</Link>
        </div>
      </header>

      <form onSubmit={onSearch} style={{ display: 'flex', gap: 8, margin: '0.5rem 0 1rem' }}>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name…"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)' }}
        />
        <button type="submit" className="primary-btn">Search</button>
        {q && <button type="button" className="secondary-btn" onClick={() => { setQ(''); load(''); }}>Clear</button>}
      </form>

      {error && <div className="tt-status error">{error}</div>}
      {loading && <div className="tt-status saving">Loading…</div>}

      {!loading && users.length === 0 && <div className="tt-empty">No users found.</div>}

      {!loading && users.length > 0 && (
        <div style={{ overflowX: 'auto', background: 'var(--card)', border: '1px solid var(--chip-border)', borderRadius: 12 }}>
          <table className="admin-users-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem' }}>
            <thead>
              <tr style={{ background: 'var(--surface-soft)', textAlign: 'left' }}>
                <th style={th}>Email</th>
                <th style={th}>Name</th>
                <th style={th}>Role</th>
                <th style={th}>Joined</th>
                <th style={{ ...th, textAlign: 'right' }}>Calendars</th>
                <th style={{ ...th, textAlign: 'right' }}>Attempts</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--chip-border)' }}>
                  <td style={td}>
                    <a href={`mailto:${u.email}`} style={{ color: 'var(--link)' }}>{u.email}</a>
                  </td>
                  <td style={td}>{u.name || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                  <td style={td}>
                    {u.isAdmin
                      ? <span className="cal-tag cal-tag-public">Admin</span>
                      : <span style={{ color: 'var(--muted)' }}>user</span>}
                  </td>
                  <td style={td}>{fmtDate(u.createdAt)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{u.calendarCount}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{u.attemptCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.75rem' }}>
        {users.length} user{users.length === 1 ? '' : 's'} shown.
      </p>
    </div>
  );
}

const th = { padding: '10px 12px', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)' };
const td = { padding: '10px 12px', color: 'var(--text)', verticalAlign: 'middle' };
